import { useEventListener } from '@vueuse/core'
import { ref, toValue, watch } from 'vue'

import type {
  BarcodeScannerOptions,
  BarcodeScannerReturn
} from '@/composables/use-barcode-scanner/useBarcodeScanner.types'

import {
  SCANNER_INACTIVITY_TIMEOUT_MS,
  SCANNER_INTER_CHAR_DELAY_MS
} from '@/composables/use-barcode-scanner/useBarcodeScanner.consts'
import { markScanEndedByEnter } from '@/composables/use-barcode-scanner/useScannerEnterGuard'
import { usePasteDetection } from '@/composables/use-paste-detection/usePasteDetection'
import { isStrictBarcode, isValidSingleBarcode } from '@/domains/command-center/utils/barcodeUtils'

const useBarcodeScanner = (options: BarcodeScannerOptions): BarcodeScannerReturn => {
  const {
    onScan,
    interCharDelay = SCANNER_INTER_CHAR_DELAY_MS,
    inactivityTimeout = SCANNER_INACTIVITY_TIMEOUT_MS,
    ignoreInputFocus = true,
    validate = isValidSingleBarcode,
    // Paste is judged on shape, keydown on timing — see the note above `usePasteDetection` below.
    validatePaste = isStrictBarcode
  } = options

  const enabled = ref(toValue(options.enabled ?? true))
  const scannedBarcodes = ref<string[]>([])
  const lastScanned = ref<string | undefined>()

  let buffer = ''
  let lastKeyTime = 0
  let inactivityTimer: ReturnType<typeof setTimeout> | undefined

  // Sync external enabled state using MaybeRefOrGetter pattern
  watch(
    () => toValue(options.enabled ?? true),
    val => {
      enabled.value = val
    }
  )

  const isInputFocused = (): boolean => {
    const el = document.activeElement
    if (!el) return false
    if (el.hasAttribute('data-barcode-passthrough')) return false
    const tag = el.tagName.toLowerCase()
    return tag === 'input' || tag === 'textarea' || el.hasAttribute('contenteditable')
  }

  const resetBuffer = () => {
    buffer = ''
    if (inactivityTimer) {
      clearTimeout(inactivityTimer)
      inactivityTimer = undefined
    }
  }

  /** True when the buffer held a valid code and it went to `onScan`. */
  const processScan = (): boolean => {
    const barcode = buffer.trim()
    resetBuffer()

    if (!barcode || !validate(barcode)) return false

    scannedBarcodes.value = [...scannedBarcodes.value, barcode]
    lastScanned.value = barcode
    void onScan(barcode)

    return true
  }

  const scheduleInactivityFlush = () => {
    if (inactivityTimer) clearTimeout(inactivityTimer)
    inactivityTimer = setTimeout(processScan, inactivityTimeout)
  }

  useEventListener(document, 'keydown', (event: KeyboardEvent) => {
    if (!enabled.value) return
    if (ignoreInputFocus && isInputFocused()) return
    if (event.metaKey || event.ctrlKey || event.altKey) return

    const now = Date.now()

    if (event.key === 'Enter') {
      if (buffer.length > 0) {
        event.preventDefault()
        // The Enter guard's clock misses a slow scanner's Enter, so the pipeline tells it which Enter ended a scan.
        if (processScan()) markScanEndedByEnter()
      }
      return
    }

    if (event.key.length !== 1) return

    if (buffer.length > 0 && now - lastKeyTime > interCharDelay) {
      resetBuffer()
    }

    buffer += event.key
    lastKeyTime = now
    scheduleInactivityFlush()
  })

  /**
   * ⚠ **Paste is validated strictly, and keydown is not.** They look alike and are not.
   *
   * A keydown burst arriving under 50ms per character could only have come from a scanner, so its
   * shape barely matters. A paste is a person pressing Cmd+V — the same act as typing — so its
   * shape is the only evidence there is. Judging both by the loose rule meant pasting an order
   * number ran the product-only barcode pipeline, and the order was never searched. An order
   * number is long and invented, so pasting it is exactly what people do.
   *
   * A real barcode pasted from a spreadsheet still works: it is 8, 12, 13 or 14 digits.
   */
  usePasteDetection({
    onPaste: candidates => {
      for (const candidate of candidates) {
        scannedBarcodes.value = [...scannedBarcodes.value, candidate]
        lastScanned.value = candidate
        void onScan(candidate)
      }
    },
    validate: validatePaste,
    ignoreInputFocus,
    enabled: options.enabled
  })

  const clearHistory = () => {
    scannedBarcodes.value = []
    lastScanned.value = undefined
  }

  return {
    scannedBarcodes,
    lastScanned,
    clearHistory,
    enabled
  }
}

export { useBarcodeScanner }
